import { type FC } from "react"

import type { WebsiteEditProposedChange } from "@forento/core/models/aiWebsite"
import {
	landingPageAnimateInStyleLabels,
	landingPageButtonStyles,
	landingPageFonts,
} from "@forento/core/models/landing"
import { blockDefinition } from "@forento/core/models/landingBlock"
import { landingPageStyles } from "@forento/core/models/landingStyle"

import AiProposedChanges, { BeforeAfter, ContentBlock, ExpandableRow, PlainRow } from "~/components/AiProposedChanges"

type Props = {
	changes: WebsiteEditProposedChange[]
	decision: "applied" | "discarded" | null
	isApplying: boolean
	isDiscarding: boolean
	onApply(): void
	onDiscard(): void
}

const ProposedChanges: FC<Props> = ({ changes, decision, isApplying, isDiscarding, onApply, onDiscard }) => (
	<AiProposedChanges
		changeCount={changes.length}
		decision={decision}
		isApplying={isApplying}
		isDiscarding={isDiscarding}
		onApply={onApply}
		onDiscard={onDiscard}
	>
		{changes.map((change, index) => (
			<ChangeRow key={index} change={change} />
		))}
	</AiProposedChanges>
)

const ChangeRow: FC<{ change: WebsiteEditProposedChange }> = ({ change }) => {
	switch (change.type) {
		case "brand-color":
			return (
				<PlainRow label="Brand color" detail="Also used across your academy, not only the website">
					<div className="mt-1.5 flex items-center gap-2 text-xs text-gray-700">
						<ColorSwatch color={change.before} />
						<span>→</span>
						<ColorSwatch color={change.after} />
					</div>
				</PlainRow>
			)
		case "site-style":
			return (
				<PlainRow
					label="Website style"
					detail={`${landingPageStyles[change.before].label} → ${landingPageStyles[change.after].label}`}
				/>
			)
		case "site-brand-color":
			return (
				<PlainRow
					label="Academy brand color on the website"
					detail={`${change.before ? "Used" : "Not used"} → ${change.after ? "Used" : "Not used"}`}
				/>
			)
		case "site-font":
			return (
				<PlainRow
					label="Website font"
					detail={`${landingPageFonts[change.before].label} → ${landingPageFonts[change.after].label}`}
				/>
			)
		case "site-button-style":
			return (
				<PlainRow
					label="Button style"
					detail={`${landingPageButtonStyles[change.before].label} → ${landingPageButtonStyles[change.after].label}`}
				/>
			)
		case "site-animation":
			return (
				<PlainRow
					label="Section animation"
					detail={`${landingPageAnimateInStyleLabels[change.before]} → ${landingPageAnimateInStyleLabels[change.after]}`}
				/>
			)
		case "page-title":
			return <PlainRow label={`Renamed page "${change.before}" to "${change.after}"`} detail={change.pagePath} />
		case "page-description":
			if (change.after === null) {
				return <PlainRow label="SEO description now uses the academy description" detail={change.pagePath} />
			}
			return (
				<ExpandableRow label="SEO description updated" detail={change.pagePath}>
					<BeforeAfter before={change.before} after={change.after} />
				</ExpandableRow>
			)
		case "block-rewrite":
			return (
				<ExpandableRow
					label={`Rewrote the ${blockDefinition(change.blockType).label} section`}
					detail={change.pageTitle}
				>
					<BeforeAfter before={change.before} after={change.after} />
				</ExpandableRow>
			)
		case "block-create":
			return (
				<ExpandableRow
					label={`New ${blockDefinition(change.blockType).label} section`}
					detail={change.pageTitle}
				>
					<ContentBlock tone="added" title="Content">
						{change.content}
					</ContentBlock>
				</ExpandableRow>
			)
		case "block-delete":
			return (
				<ExpandableRow
					label={`Removed the ${blockDefinition(change.blockType).label} section`}
					detail={change.pageTitle}
				>
					<ContentBlock tone="removed" title="Removed content">
						{change.content}
					</ContentBlock>
				</ExpandableRow>
			)
	}
}

const ColorSwatch: FC<{ color: string }> = ({ color }) => (
	<span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white py-0.5 pr-2 pl-0.5">
		<span className="size-4 rounded-full border border-black/10" style={{ backgroundColor: color }} />
		<span className="font-medium uppercase">{color}</span>
	</span>
)

export default ProposedChanges
